'use client'

/**
 * SaiChatDotAnimated — the design system's Rive-powered SchoolAI Dot, for the
 * primary conversational surfaces only (issue #13332).
 *
 * `@sai/ui/components/dot-abstract` owns the Rive runtime, the WASM
 * setup, device-pixel-ratio handling and the hosted
 * `rive-animations/dot-abstract.riv` asset, plus the `State Machine 1`
 * triggers this file drives. Nothing here recreates the motion in CSS, and
 * nothing here adds a second Rive dependency: `@rive-app/react-webgl2@4.23.0`
 * is a direct dependency OF `@sai/ui`, resolved through it.
 *
 * ## Four gates, each of which renders the static Dot instead
 *
 * 1. **Server render / first client render.** The Rive module is imported
 *    dynamically from an effect, so the initial HTML is the static Dot and
 *    hydration matches it byte for byte. A canvas cannot be server-rendered
 *    and `RuntimeLoader.setWasmUrl` in that module is `typeof window`-guarded
 *    for the same reason.
 *
 * 2. **`prefers-reduced-motion: reduce`.** Static Dot, and no state-machine
 *    triggers at all — reactive, so flipping the OS setting mid-session
 *    unmounts the canvas rather than waiting for a reload.
 *
 * 3. **A failed `.riv` load**, via `DotAbstract`'s `onError`, or a rejected
 *    dynamic `import()` (the chunk did not load). Note the boundary: `onError`
 *    forwards `useRive`'s `onLoadError`, which reports a `.riv` that would not
 *    load and **not** a WASM runtime abort — that second case is gate 4's, and
 *    conflating the two is what left a blank canvas. Both land on the static
 *    Dot at the same size and neither
 *    produces an unhandled rejection, so the chat affordance around it stays
 *    visible and operable. The failure is not reported through
 *    `reportClientError`: the package already logs it to the console, this is
 *    a decorative animation with a pixel-identical fallback, and a new
 *    `ERROR_IDS` member would drag in the `client-reportable-error-ids`
 *    allowlist for no diagnostic anyone would act on.
 *
 * 4. **The design system's assets are not CORS-readable from EVERY origin.**
 *    `r2.schoolai.com` serves `rive-animations/dot-abstract.riv` and
 *    `rive-2.31.0.wasm` behind an `Origin` **allowlist**, not with a wildcard
 *    and not with the header missing. Measured 2026-09-08, per-origin:
 *
 *    | Origin | `Access-Control-Allow-Origin` |
 *    |---|---|
 *    | `https://cadence.schoolai.com` | echoed — allowed |
 *    | `http://localhost:3000` | echoed — allowed |
 *    | `http://localhost:<any other port>` | absent — blocked |
 *    | `https://*.up.railway.app` | absent — blocked |
 *
 *    So production and a default-port local dev server animate, while a
 *    per-issue worktree (`3000 + issue % 1000`) and every Railway preview
 *    deploy do not. That is a real, permanent-until-someone-edits-the-bucket
 *    degradation on the preview tier, which is why this gate is a PRE-flight
 *    rather than a reaction: `onError` does NOT fire for a blocked WASM. It is
 *    wired only to `useRive`'s `onLoadError`
 *    (`dist/components/dot-abstract.js:19`), which does not report a WASM
 *    runtime abort — so without the probe the canvas mounted and stayed BLANK
 *    (`canvas.toDataURL().length === 190`), which is strictly worse than the
 *    static gradient it replaced. The probe costs one memoised `HEAD` per page
 *    load and needs no per-origin knowledge: it asks the browser the same
 *    question the browser is about to answer for Rive.
 *
 * ## Why this shipped a CSP change with it
 *
 * Rive instantiates its runtime with `WebAssembly.instantiateStreaming`, which
 * CSP forbids unless `script-src` carries `'wasm-unsafe-eval'`. It is tempting
 * to defer that on the grounds that `CSP_MODE` is unset in Railway production
 * and the header is therefore `Content-Security-Policy-Report-Only` — but
 * report-only still *reports*, and `report-uri /api/csp-report` is
 * unconditional in the policy. Every Dot mount would have POSTed a violation
 * report to a route whose `RATE_LIMIT_MAX` is 10 per IP per 60s, which both
 * wastes the budget and masks genuine reports. So the token is added in
 * `src/proxy.ts` and its `next.config.js` mirror, in this PR.
 *
 * `'wasm-unsafe-eval'` is not `'unsafe-eval'`: it admits WebAssembly
 * instantiation and nothing else, so the `csp-headers` e2e assertion that
 * `script-src` must not contain `'unsafe-eval'` still holds — the token it
 * greps for is quoted, and `'wasm-unsafe-eval'` has a `-` where that pattern
 * needs the opening quote.
 */

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Ref,
} from 'react'
import { getRiveWasmUrl } from '@sai/ui/lib/get-cdn-url'
import type {
  DotAbstractHandle,
  DotAbstractTrigger,
} from '@sai/ui/components/dot-abstract'
import { SaiChatDot } from './sai-chat-dot'

type DotAbstractComponent =
  typeof import('@sai/ui/components/dot-abstract')['DotAbstract']

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Memoised for the module, i.e. once per page load rather than once per Dot:
 * Today alone renders four (composer, FAB, panel header, empty state), and the
 * answer cannot differ between them.
 */
let assetReachability: Promise<boolean> | null = null

/**
 * Probes the Rive **WASM runtime**, not the `.riv` file, and the choice is
 * deliberate: of the two cross-origin assets the package fetches, the WASM is
 * the one whose failure is SILENT. A `.riv` that will not load reaches
 * `useRive`'s `onLoadError` and so reaches our `onError` (gate 3); a WASM
 * runtime abort reaches neither, and leaves a mounted blank canvas.
 *
 * One probe covers both because the bucket's allowlist is keyed on `Origin`
 * and not on path — verified per-origin against both
 * `rive-animations/dot-abstract.riv` and `rive-2.31.0.wasm`, which return
 * identical `Access-Control-Allow-Origin` behaviour and carry `vary: Origin`.
 *
 * `HEAD`, because the WASM is ~1.8MB and this asks a question about headers.
 * The bucket's preflight advertises `access-control-allow-methods: GET, HEAD`,
 * and a `HEAD` with no custom headers is a CORS-simple request, so it needs no
 * preflight of its own.
 */
function isRiveAssetReachable(): Promise<boolean> {
  // Started from `Promise.resolve()` so a `fetch` that throws synchronously
  // (or a stub that returns no promise) lands in the same `catch` instead of
  // escaping as an unhandled rejection from the caller's fire-and-forget IIFE.
  assetReachability ??= Promise.resolve()
    .then(() => fetch(getRiveWasmUrl(), { method: 'HEAD' }))
    // A CORS-blocked cross-origin fetch REJECTS rather than resolving non-ok,
    // so both arms matter: `.ok` catches a 404/5xx, the `catch` catches the
    // CORS block, an offline client and a DNS failure.
    .then(response => response.ok)
    .catch(() => false)
  return assetReachability
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

/**
 * What a caller can do to the Dot imperatively. Narrower than
 * `DotAbstractHandle` in one way that matters: calling `trigger` while the Dot
 * is static (any of the four gates above) is a no-op rather than a throw, so
 * no caller has to know which variant it got.
 */
export interface SaiChatDotHandle {
  trigger: (trigger: DotAbstractTrigger) => void
}

interface SaiChatDotAnimatedProps {
  /** Rendered size in px. Must match what the static Dot occupied. */
  size: number
  /**
   * Reactive streaming state, mapped onto the imperative
   * `thinkingStart`/`thinkingEnd` triggers on every TRANSITION.
   *
   * Declarative on purpose: the panel sets its `isStreaming` mirror false in a
   * `finally`, so success, HTTP failure, an SSE `error` frame and an aborted
   * request all arrive here as `true -> false` and all fire `thinkingEnd`.
   * Hand-placing `trigger('thinkingEnd')` at each terminal path is how the
   * abort path (which `catch` returns from early) would have been missed.
   */
  thinking?: boolean
  ref?: Ref<SaiChatDotHandle>
}

export function SaiChatDotAnimated({
  size,
  thinking = false,
  ref,
}: SaiChatDotAnimatedProps) {
  const [DotAbstract, setDotAbstract] = useState<DotAbstractComponent | null>(
    null,
  )
  const [loadFailed, setLoadFailed] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const dotRef = useRef<DotAbstractHandle | null>(null)
  /**
   * What the CURRENT handle has been told. Reset on every new handle, because
   * a new handle has been told nothing and a fresh Rive canvas starts in its
   * default non-thinking state — which is also what `false` means here.
   */
  const appliedThinkingRef = useRef(false)
  /**
   * Bumped whenever `DotAbstract` hands us a new handle, so the trigger effect
   * below re-runs against it. A ref mutation alone would not.
   */
  const [dotGeneration, setDotGeneration] = useState(0)

  /**
   * A CALLBACK ref, not a ref object, and that is the fix for a silent
   * swallowed `thinkingStart`.
   *
   * `DotAbstract` builds its handle out of `useViewModelInstanceTrigger(...)`
   * callables whose identity changes when Rive binds the view-model instance
   * (`useImperativeHandle(ref, …, [triggers])`). BEFORE that bind, firing one
   * is a **silent no-op**: rive's property operation body is
   * `if (i && p.current === t) {…} if (p.current) {…}` and both `i` and
   * `p.current` are null, so neither branch runs — and because the hook still
   * returns a truthy function, `DotAbstract`'s own
   * `if (fire) … else console.warn` never reaches the warn either. There is no
   * error, no rejection and no return value to test.
   *
   * So the trigger cannot be fired once and assumed delivered. React re-invokes
   * a callback ref when the imperative handle is rebuilt, which is exactly the
   * bind moment, and that is the signal this uses to re-apply the desired
   * state on a handle that can actually receive it.
   */
  const attachDot = useCallback((handle: DotAbstractHandle | null) => {
    dotRef.current = handle
    appliedThinkingRef.current = false
    if (handle) setDotGeneration(generation => generation + 1)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const query = window.matchMedia(REDUCED_MOTION_QUERY)
    setReduceMotion(query.matches)
    const onChange = () => setReduceMotion(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (DotAbstract) return
    // Read live rather than through `reduceMotion`: the effect above may not
    // have committed its state yet on this pass, and both effects run in the
    // same commit. `reduceMotion` stays in the dep list so a later change to
    // the preference re-runs this and loads the module.
    if (prefersReducedMotion()) return

    let cancelled = false
    void (async () => {
      // Reachability BEFORE the chunk: when the assets are blocked there is no
      // point paying for the Rive runtime bundle either.
      if (!(await isRiveAssetReachable())) {
        if (!cancelled) setLoadFailed(true)
        return
      }
      try {
        const module = await import('@sai/ui/components/dot-abstract')
        // Updater form: a bare component value would be called as an updater.
        if (!cancelled) setDotAbstract(() => module.DotAbstract)
      } catch {
        // Chunk load failed — see sub-issue R carve-out
        // The static Dot is the fallback and the rejection carries no
        // diagnostic anyone would act on; see gate 3 in the header comment.
        if (!cancelled) setLoadFailed(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [DotAbstract, reduceMotion])

  const animated = DotAbstract !== null && !loadFailed && !reduceMotion

  useEffect(() => {
    if (!animated) {
      // Leaving the animated branch unmounts the canvas. The next one is a
      // fresh Rive instance in its default non-thinking state, so nothing has
      // been applied to it — without this reset, a reduced-motion flip on and
      // back off mid-stream left the remounted Dot idle for the rest of the
      // request.
      appliedThinkingRef.current = false
      return
    }
    if (thinking === appliedThinkingRef.current) return
    appliedThinkingRef.current = thinking
    dotRef.current?.trigger(thinking ? 'thinkingStart' : 'thinkingEnd')
    // `dotGeneration` is load-bearing, not incidental: it re-runs this effect
    // when Rive binds and `DotAbstract` rebuilds its handle, which is the only
    // point at which a trigger stops being a silent no-op. See `attachDot`.
  }, [animated, thinking, dotGeneration])

  useImperativeHandle(
    ref,
    () => ({
      trigger: (trigger: DotAbstractTrigger) => {
        dotRef.current?.trigger(trigger)
      },
    }),
    [],
  )

  const handleError = useCallback(() => setLoadFailed(true), [])

  if (!animated || !DotAbstract) {
    return <SaiChatDot size={size} />
  }

  return (
    <DotAbstract
      aria-hidden="true"
      // `pointer-events-none` because the package's root div is
      // `cursor-pointer` and every one of these Dots sits inside the control
      // that owns the interaction (the FAB button, the composer's input row).
      // Without it the canvas would claim the cursor and the hit target.
      className="pointer-events-none shrink-0"
      data-testid="sai-chat-dot"
      onError={handleError}
      ref={attachDot}
      size={size}
    />
  )
}
